'use client';

import { useTranslations } from 'next-intl';
import { type FormEvent, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { useAnnouncer } from '@/components/ui/announcer';
import { InputField } from '@/components/ui/input-field';
import { ModalFooter } from '@/components/ui/modal-footer';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { TOAST_IDS } from '@/config/toast-ids';
import { runWithCleanup } from '@/lib/async/run-with-cleanup';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';

interface CreateWalletProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateWallet: (label: string) => Promise<void>;
}

export function CreateWallet({
  open,
  onOpenChange,
  onCreateWallet,
}: CreateWalletProps): React.ReactNode {
  const t = useTranslations('wallet.create');
  const { announce } = useAnnouncer();
  const { enabled: multiWalletsEnabled, canStartOperation } = useMultiWalletFeature();
  const [label, setLabel] = useState<string>('');
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const trimmed = label.trim();
    if (trimmed.length === 0) return;
    if (!canStartOperation('create', 'wallet_management')) {
      onOpenChange(false);
      return;
    }

    setIsCreating(true);
    setError(null);

    await runWithCleanup(
      async () => {
        try {
          await onCreateWallet(trimmed);
          announce(`Wallet created: ${trimmed}`);
          showToast({
            id: TOAST_IDS.WALLET_CREATED,
            message: t('success'),
            variant: 'success',
          });
          setLabel('');
          onOpenChange(false);
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : String(err);
          logger.error('Wallet creation failed', { error: message.slice(0, 200) });
          setError(message);
        }
      },
      () => setIsCreating(false),
    );
  }

  return (
    <SilkSheet
      open={open && multiWalletsEnabled}
      onOpenChange={onOpenChange}
      variant="detached"
      size="md"
      keyboard
      title={t('title')}
      description={t('description')}
      sheetClassName="sm:aspect-[4/3]"
      footer={
        <ModalFooter>
          <AnimatedButton
            variant="secondary"
            onClick={(): void => onOpenChange(false)}
            disabled={isCreating}
          >
            {t('cancel')}
          </AnimatedButton>
          <AnimatedButton
            type="submit"
            form="create-wallet-form"
            disabled={isCreating || label.trim().length === 0}
            loading={isCreating}
          >
            {isCreating ? t('creating') : t('create_button')}
          </AnimatedButton>
        </ModalFooter>
      }
    >
      <form id="create-wallet-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        <InputField
          id="wallet-name"
          label={t('label')}
          type="text"
          value={label}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
            setLabel(e.target.value);
            setError(null);
          }}
          placeholder={t('placeholder')}
          disabled={isCreating}
          autoComplete="off"
          data-dd-privacy="mask"
          error={error ?? undefined}
        />
      </form>
    </SilkSheet>
  );
}
